import React, { useState, useEffect, useCallback } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import BaseButton from "../../../components/Common/Buttons/BaseButton";
import SelectBox from "../../../components/Common/Form/SelectBox";
import { EcReturn, ProjectInformation, DiscountSetting } from "../../../types/pages/EcWebsettlement/purchase_ec_return";
import { PurchaseEcReturnFormValues } from "../../../types/pages/EcReturn/Websettlement/purchase_ec_return";
import { Control, UseFormGetValues, UseFormWatch } from "react-hook-form";
import EcReturnForm from "./SelectReturn/EcReturnForm";
import CouponArea from "./CouponArea";
import { HttpRequest } from "../../../utils/common";
import CouponBannerIntroduction500 from "../../../../images/紹介割引_500円_1280_480.png";
import CouponBannerIntroduction1000 from "../../../../images/紹介割引_1000円_1280_480.png";
import CouponBannerRepeat500 from "../../../../images/リピート割引_500円_1280_480.png";
import CouponBannerRepeat1000 from "../../../../images/リピート割引_1000円_1280_480.png";
import CouponBannerCross500 from "../../../../images/応援ハシゴ割引_500円_1280_480.png";
import CouponBannerCross1000 from "../../../../images/応援ハシゴ割引_1000円_1280_480.png";
import CouponBannerBulk500 from "../../../../images/まとめ買い割引_500円_1280_480.png";
import CouponBannerBulk1000 from "../../../../images/まとめ買い割引_1000円_1280_480.png";

type Props = {
  ecReturns: EcReturn[],
  projectInformation: ProjectInformation,
  control: Control<PurchaseEcReturnFormValues, any>,
  setValue: any,
  getValues: UseFormGetValues<PurchaseEcReturnFormValues>,
  watch: UseFormWatch<PurchaseEcReturnFormValues>,
  totalAmount: () => number,
  priceByAmount: () => number[],
  totalPriceByAmount: () => number,
  commission: number,
  is_draft?: boolean,
  discount_setting?: DiscountSetting,
  is_target_for_discount: boolean,
}

const SelectReturn = ({
  ecReturns,
  projectInformation,
  control,
  setValue,
  getValues,
  watch,
  totalAmount,
  totalPriceByAmount,
  priceByAmount,
  commission,
  is_draft,
  discount_setting,
  is_target_for_discount
}: Props) => {
  const [isTotalAmountValid, setIsTotalAmountValid] = useState<boolean>(totalAmount() >= 500);
  const [couponError, setCouponError] = useState<string>("");

  const discountAmount = discount_setting?.discount_amount
  const discountBannersMap: Record<string, Record<number, string>> = {
    repeat: {
      500: CouponBannerRepeat500,
      1000: CouponBannerRepeat1000,
    },
    cross: {
      500: CouponBannerCross500,
      1000: CouponBannerCross1000,
    },
    bulk: {
      500: CouponBannerBulk500,
      1000: CouponBannerBulk1000,
    },
    introduction: {
      500: CouponBannerIntroduction500,
      1000: CouponBannerIntroduction1000,
    },
  };

  const discountBanner = discountBannersMap[discount_setting?.discount_type]?.[discount_setting?.discount_amount];

  const appliedCoupon = watch("appliedCoupon");

  const handleApplyCoupon = (code: string) => {
    setCouponError("");
    
    HttpRequest.post(
      `/api/investments/${projectInformation.investment_id}/validate_coupon`,
      { coupon_code: code }
    )
      .then(response => {
        const discount = response.data.discount_amount;
        if (discount > totalAmount()) {
          setCouponError("クーポンの割引額が合計金額を超えています");
          setValue("appliedCoupon", null);
          return;
        }

        setValue("appliedCoupon", { code, amount: response.data.discount_amount });
      })
      .catch(error => {
        if (error.response?.status === 422) {
          const msg = error.response.data?.message ?? 
                     error.response.data?.error ?? 
                     "クーポンの適用に失敗しました";
          setCouponError(msg);
        } else {
          setCouponError("通信エラーが発生しました");
        }
        setValue("appliedCoupon", null);
      });
  };

  const handleRemoveCoupon = () => {
    setValue("appliedCoupon", null);
    setCouponError("");
  };
  
  const updateTotalAmountValidity = useCallback(() => {
    setIsTotalAmountValid(totalAmount() >= 500);
  }, [totalAmount]);

  useEffect(() => {
    const subscription = watch((data) => {
      updateTotalAmountValidity();
    });
    return () => subscription.unsubscribe();
  }, [watch, updateTotalAmountValidity]);

  const additionalAmountOptions = [
    "0",
    "500",
    "1,000",
    "1,500",
    "2,000",
    "2,500",
    "3,000",
    "3,500",
    "4,000",
    "4,500",
    "5,000",
    "5,500",
    "6,000",
    "6,500",
    "7,000",
    "7,500",
    "8,000",
    "8,500",
    "9,000",
    "9,500",
    "10,000",
    "20,000",
    "30,000",
    "40,000",
    "50,000",
    "60,000",
    "70,000",
    "80,000",
    "90,000",
    "100,000",
    "200,000",
    "300,000",
    "400,000",
    "500,000",
    "600,000",
    "700,000",
    "800,000",
    "900,000",
    "1,000,000",
    "2,000,000",
    "3,000,000",
    "4,000,000",
    "5,000,000",
    "6,000,000",
    "7,000,000",
    "8,000,000",
    "9,000,000"
  ]

  const additionalAmountValues = [
    0,
    500,
    1000,
    1500,
    2000,
    2500,
    3000,
    3500,
    4000,
    4500,
    5000,
    5500,
    6000,
    6500,
    7000,
    7500,
    8000,
    8500,
    9000,
    9500,
    10000,
    20000,
    30000,
    40000,
    50000,
    60000,
    70000,
    80000,
    90000,
    100000,
    200000,
    300000,
    400000,
    500000,
    600000,
    700000,
    800000,
    900000,
    1000000,
    2000000,
    3000000,
    4000000,
    5000000,
    6000000,
    7000000,
    8000000,
    9000000
  ]

  return (
    <>
      { is_target_for_discount && discount_setting?.discount_type !== "introduction" && discountBanner && (
        <div className="mb-3">
          <img
            src={discountBanner}
            className="object-contain"
          />
        </div>
      )}
      <div className="mb-[30px] flex">
        <div className="h-[40px] bg-[#1F2122] w-[61%] relative flex items-center justify-center text-white text-sm font-bold">
          <span className="text-[#1F2122] bg-[#768084] rounded-[50%] inline-block mr-1 w-5 h-5 text-center">
            1
          </span>
          支援内容の入力
          <div className="right-triangle-thin right-[-7px] absolute"></div>
        </div>
        <div className="w-[13%] border-[#1F2122] flex items-center justify-center border-b border-r border-t">
          2
        </div>
        <div className="w-[13%] border-[#1F2122] flex items-center justify-center border-b border-r border-t">
          3
        </div>
        <div className="w-[13%] border-[#1F2122] flex items-center justify-center border-b border-r border-t">
          4
        </div>
      </div>
      {ecReturns.map((ecReturn, index) => (
        <EcReturnForm
          ecReturn={ecReturn}
          ecReturnIndex={index}
          ecReturnLength={ecReturns.length}
          control={control}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          priceByAmount={priceByAmount}
        />
      ))}
      <div className="pt-[40px]">
        <div className="mb-10 p-5 bg-[#F4F6F8]">
          <p className="font-bold mb-2">リターンに関するお問い合わせ先</p>
          <a href={`mailto:${projectInformation.owner_email}`} className="link-hover-none underline">{projectInformation.owner_email}</a>
        </div>
        <div className="bg-[#F4F6F8] mb-10 p-5">
          <p className="mb-5 text-sm font-medium">
            あなたの気持ちをリターン購入金額に上乗せしよう
          </p>
          <div className="mb-[10px] flex items-end justify-between">
            <SelectBox
              id="additional_amount"
              name="additional_amount"
              placeholder="上乗せ金額を選択してください"
              control={control}
              options={additionalAmountOptions}
              values={additionalAmountValues}
            />
            <span className="font-medium ml-1">円</span>
          </div>
          <p className="text-[#768084] text-xs">
            ※500円以上の支援をお願いします
          </p>
        </div>
        {is_target_for_discount && discount_setting?.discount_type === "introduction" && (
          <CouponArea
            appliedCoupon={appliedCoupon || { code: "", amount: 0 }}
            onApplyCoupon={handleApplyCoupon}
            onRemoveCoupon={handleRemoveCoupon}
            error={couponError}
          />
        )}
        { appliedCoupon && discountBanner && (
          <div className="mt-3">
            <img
              src={discountBanner}
              className="object-contain"
            />
          </div>
        )}
        <div className="pb-[40px] pt-[40px] border-[#D2DFE6] border-b">
          <p className="font-bold">合計の支援金額(税込)</p>
          <div className="text-[34px] mb-[10px] text-right font-bold">
          { (totalAmount()).toLocaleString() }
            <span className="text-lg">円</span>
          </div>
          <p className="text-[#768084] text-sm mb-1">内訳:</p>
          <div className="flex items-end flex-col">
            <div className="text-xs mb-1">
              リターンの購入金額: {totalPriceByAmount().toLocaleString()}円
            </div>
            <div className="text-xs mb-1">
              上乗せ支援:{Number(watch("additional_amount")).toLocaleString()}円
            </div>
            <div className="text-xs">
              プラットフォーム利用料:{commission.toLocaleString()}円
            </div>
            {(is_target_for_discount && discount_setting?.discount_type !== "introduction" || appliedCoupon) && (
              <div className="text-xs text-red-500 mt-1">
                クーポンの割引金額: -{discountAmount.toLocaleString()}円
              </div>
            )}
          </div>
        </div>
        <div className="pt-[40px]">
          <div className="sm:mb-[60px] mb-10">
            <p className="font-bold mb-6">プラットフォーム利用料とは</p>
            <p className="mb-[10px]">プラットフォーム利用料は、その一部を『スポチュニティ基金』として社会貢献活動を行う団体へ寄付すると共に、安心安全な運営及びシステム環境の提供のために利用します。<br /><br />寄付の実施可否および時期は、スポチュニティにより決定され、その実施を保証するものではありません。</p>
            <p>詳しくは
              <a href="https://corp.spportunity.com/platform-fee/" target="_blank" rel="noopener noreferrer" className="underline link-hover-none">
                こちら
              </a>
            </p>
          </div>
          <div className="sm:mb-[60px] mb-10">
            <p className="font-bold mb-6">リターンについて</p>
            <p>リターンはご支援いただいた方、全員へお渡しいたします。</p>
            <p>リターンが配布されるのは必ずしも購入直後ではありませんのでご注意下さい。</p>
            <p>スポチュニティはプロジェクトオーナーのプロジェクト実行能力を保証するものではありません。プロジェクトの実行やリターンの発送（履行）などは、全てプロジェクトオーナーの責任のもと行われます。</p>
          </div>
        </div>
      </div>
      <div className="pt-[40px]">
        <p className="text-[#768084] mb-[40px] text-xs">
          リターンの購入にあたり、
          <a
            href="https://corp.spportunity.com/term_of_use/"
            className="link-hover-none underline"
          >
            スポチュニティのサービス利用規約
          </a>
          および
          <a
            href="https://corp.spportunity.com/privacy_policy/"
            className="link-hover-none underline"
          >
            プライバシーポリシー
          </a>
          に同意したものとみなされます 。
        </p>
        <div className="mb-[60px] sm:mx-auto h-[50px] sm:w-[290px]">
          <BaseButton
            text="お支払い情報の入力へ進む"
            fontSize="text-sm"
            isSubmit={true}
            disabled={!isTotalAmountValid || is_draft}
          />
          { !isTotalAmountValid && (
            <p className="text-xs text-red-500 mt-1 text-center">
              合計支援金額が500円に満たないため、このままお手続きを進めることができません。
            </p>
          )}
        </div>
        <div className="text-center mt-10">
          <a
            href={is_draft ? `${projectInformation.investment_path}${window.location.search}` : projectInformation.investment_path}
            className="left-icon icon-stick-arrow-left text-sm font-medium"
          >
            プロジェクトの詳細ページに戻る
          </a>
        </div>
      </div>
    </>
  );
};

export default SelectReturn;
